import { useState } from 'react';

import { usePatchProgramBroadcast } from '@/features/broadcast-schedule-program/api/patch-broadcast-schedule';
import { useUnlinkAudienceFileFromProgramBroadcast } from '@/features/broadcast-schedule-program/api/unlink-audience-file-from-program-broadcast';
import { usePostProgram } from '@/features/programs/api/post-program';
import { TProgram, TProgramForm } from '@/features/programs/types';
import { useDisclosureWithData } from '@/hooks/useDisclosureWithData';
import { DrawerFormDataType } from '@/types';

import { useDeleteProgramBroadcast } from '../api/delete-program_broadcast';
import { useCreateProgramBroadcast } from '../api/post-program-broadcast';
import { ProgramBroadcastDrawerFormData, TProgramBroadcast, TProgramBroadcastForm } from '../types';

type UseProgramBroadcastControllerParams = {
  onProgramCreated?: (program: TProgram) => void;
};

export function useProgramBroadcastController({
  onProgramCreated,
}: UseProgramBroadcastControllerParams = {}) {
  const programDrawer = useDisclosureWithData<DrawerFormDataType<Partial<TProgramForm>>>();
  const programBroadcastDialog = useDisclosureWithData<TProgramBroadcast>();
  const { mutate: deleteProgramBroadcast } = useDeleteProgramBroadcast({
    config: {
      onSuccess: () => {
        programBroadcastDialog.close();
      },
    },
  });

  const [audiencesUsed, setAudiencesUsed] = useState<string[]>([]);
  const programBroadcastDrawer = useDisclosureWithData<
    DrawerFormDataType<ProgramBroadcastDrawerFormData>
  >({
    onClose: () => {
      setAudiencesUsed([]);
    },
  });
  const unlinkAudienceFileDrawer = useDisclosureWithData<TProgramBroadcast>();

  const { mutate: createProgram, isPending: isCreatingProgram } = usePostProgram({
    config: {
      onSuccess: program => {
        programDrawer.close();
        onProgramCreated?.(program);
      },
    },
  });

  const audiences = programBroadcastDrawer.data?.data?.audiences ?? [];
  const isThelastAudienceToUse =
    audiences.length === 0 || audiences.length === audiencesUsed.length + 1;

  const { mutate: createProgramBroadcast, isPending: isCreatingProgramBroadcast } =
    useCreateProgramBroadcast();
  const { mutate: updateProgramBroadcast, isPending: isUpdatingProgramBroadcast } =
    usePatchProgramBroadcast({
      config: {
        onSuccess: () => {
          programBroadcastDrawer.close();
        },
      },
    });
  const { mutate: unlinkAudienceFileFromProgramBroadcast } =
    useUnlinkAudienceFileFromProgramBroadcast({
      config: {
        onSuccess: () => {
          unlinkAudienceFileDrawer.close();
        },
      },
    });

  const handleCreateProgram = (formData: TProgramForm) => {
    createProgram({
      ...formData,
      schedule:
        formData.schedule?.map(item => ({
          estimation_time: '',
          ...item,
        })) ?? [],
    });
  };

  const handleSubmitProgramBroadcast = (formData: TProgramBroadcastForm) => {
    const payload = {
      ...formData,
      end_datetime: formData.end_datetime.toISOString(),
      start_datetime: formData.start_datetime.toISOString(),
      delivery_datetime: formData.delivery_datetime?.toISOString(),
    };

    if (formData.id) {
      updateProgramBroadcast(payload);
      return;
    }

    createProgramBroadcast(payload, {
      onSuccess: () => {
        if (isThelastAudienceToUse) {
          programBroadcastDrawer.close();
          return;
        }

        setAudiencesUsed([...audiencesUsed, formData.audience_id]);
      },
    });
  };

  const handleUnlinkAudienceFile = () => {
    if (!unlinkAudienceFileDrawer.data) {
      return;
    }

    unlinkAudienceFileFromProgramBroadcast(unlinkAudienceFileDrawer.data.id);
  };

  const handleDeleteProgramBroadcast = () => {
    if (!programBroadcastDialog.data) {
      return;
    }
    deleteProgramBroadcast({ id: programBroadcastDialog.data.id });
  };

  return {
    audiencesUsed,

    programDrawer,
    handleCreateProgram,
    isCreatingProgram,

    programBroadcastDrawer,
    handleSubmitProgramBroadcast,
    isMutatingProgramBroadcast: isCreatingProgramBroadcast || isUpdatingProgramBroadcast,

    unlinkAudienceFileDrawer,
    handleUnlinkAudienceFile,

    programBroadcastDialog,
    handleDeleteProgramBroadcast,
  };
}
